import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Plus, Edit, Trash2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResourcesPage = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [showTrash, setShowTrash] = useState(false);
  const [trashedResources, setTrashedResources] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    description: '',
  });

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const { data } = await axios.get(`${API}/resources`, { withCredentials: true });
      setResources(data || []);
    } catch (error) {
      console.error('Error fetching resources:', error);
      toast.error(error.response?.data?.detail || 'Error al cargar recursos');
      setResources([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingResource) {
        await axios.put(`${API}/resources/${editingResource.id}`, formData, { withCredentials: true });
        toast.success('Recurso actualizado');
      } else {
        await axios.post(`${API}/resources`, formData, { withCredentials: true });
        toast.success('Recurso creado');
      }
      setDialogOpen(false);
      resetForm();
      fetchResources();
    } catch (error) {
      toast.error('Error al guardar recurso');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Enviar este recurso a la papelera? Podrás restaurarlo durante 30 días.')) return;
    try {
      await axios.delete(`${API}/resources/${id}`, { withCredentials: true });
      toast.success('Recurso movido a la papelera');
      fetchResources();
      if (showTrash) fetchTrash();
    } catch (error) {
      console.error('Error deleting resource:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar recurso');
    }
  };

  const fetchTrash = async () => {
    try {
      const { data } = await axios.get(`${API}/resources/trash`, { withCredentials: true });
      setTrashedResources(data || []);
    } catch (error) {
      setTrashedResources([]);
    }
  };

  const toggleTrash = () => {
    const next = !showTrash;
    setShowTrash(next);
    if (next) fetchTrash();
  };

  const handleRestore = async (id) => {
    try {
      await axios.post(`${API}/resources/${id}/restore`, {}, { withCredentials: true });
      toast.success('Recurso restaurado');
      fetchTrash();
      fetchResources();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restaurar');
    }
  };

  const handlePurge = async (id) => {
    if (!window.confirm('¿Eliminar este recurso DEFINITIVAMENTE? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`${API}/resources/${id}/purge`, { withCredentials: true });
      toast.success('Recurso eliminado permanentemente');
      fetchTrash();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', type: '', description: '' });
    setEditingResource(null);
  };

  const openEditDialog = (resource) => {
    setEditingResource(resource);
    setFormData({
      name: resource.name || '',
      type: resource.type || '',
      description: resource.description || '',
    });
    setDialogOpen(true);
  };

  const resourceTypes = [
    'Tubo de Burbujas',
    'Fibra Óptica',
    'Tablero Táctil',
    'Proyector',
    'Sistema de Sonido',
    'Colchoneta',
    'Elementos Aromáticos',
    'Otro',
  ];

  if (loading) return <div>Cargando...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-semibold" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
            Recursos de Sala
          </h1>
          <p className="text-sm mt-1" style={{ color: '#718096' }}>
            Gestiona los recursos disponibles en tu sala multisensorial
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={showTrash ? 'default' : 'outline'}
            onClick={toggleTrash}
            className="flex items-center gap-2"
            data-testid="resources-trash-toggle"
          >
            <Trash2 size={18} />
            Papelera{showTrash && trashedResources.length ? ` (${trashedResources.length})` : ''}
          </Button>
          <Dialog
            open={dialogOpen}
            onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}
          >
          <DialogTrigger asChild>
            <Button className="btn-primary flex items-center gap-2">
              <Plus size={20} />
              Nuevo Recurso
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingResource ? 'Editar Recurso' : 'Nuevo Recurso'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Nombre del Recurso *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="type">Tipo *</Label>
                <select
                  id="type"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  required
                  className="w-full mt-1 bg-white border border-[#E5E2DC] rounded-lg px-4 py-2"
                >
                  <option value="">Seleccionar tipo...</option>
                  {resourceTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="description">Descripción</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="mt-1"
                />
              </div>
              <Button type="submit" className="btn-primary w-full">
                {editingResource ? 'Actualizar' : 'Crear'} Recurso
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {showTrash && (
        <Card className="p-6 border-amber-200" data-testid="resources-trash-panel">
          <div className="flex items-center gap-2 mb-1">
            <Trash2 className="h-5 w-5 text-amber-600" />
            <h2 className="text-lg font-semibold text-amber-800">Papelera de Recursos ({trashedResources.length})</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Los recursos eliminados se conservan 30 días y luego se borran automáticamente.
          </p>
          {trashedResources.length === 0 ? (
            <p className="text-sm text-gray-500">No hay recursos en la papelera.</p>
          ) : (
            <div className="space-y-2">
              {trashedResources.map((r) => (
                <div key={r.id} className="flex items-center justify-between border rounded-lg px-4 py-3" data-testid={`trash-resource-${r.id}`}>
                  <div>
                    <p className="font-medium" style={{ color: '#2D3748' }}>{r.name} {r.type && <span className="text-xs text-gray-500">({r.type})</span>}</p>
                    <p className="text-xs text-gray-500">{r.days_remaining ?? 30} días restantes</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                      onClick={() => handleRestore(r.id)}
                      data-testid={`restore-resource-${r.id}`}
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Restaurar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => handlePurge(r.id)}
                      data-testid={`purge-resource-${r.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {resources.map((resource) => (
          <Card key={resource.id} className="p-6 hover:-translate-y-1 transition-transform duration-200">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
                  {resource.name}
                </h3>
                <p className="text-sm" style={{ color: '#718096' }}>
                  {resource.type}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openEditDialog(resource)}
                  className="hover:bg-[#E5E2DC]"
                >
                  <Edit size={16} style={{ color: '#5A7D6C' }} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(resource.id)}
                  className="hover:bg-red-50"
                >
                  <Trash2 size={16} style={{ color: '#F44336' }} />
                </Button>
              </div>
            </div>
            {resource.description && (
              <p className="text-sm" style={{ color: '#2D3748' }}>
                {resource.description}
              </p>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ResourcesPage;
